import React, { useState } from 'react';
import CustomCard from '../CustomCard';
import SecondaryButton from '../SecondaryButton';
import { Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { formatPhoneNumber } from '../../utils/formatPhoneNumber';
import ProfileCardOverlay from '../ProfileCardOverlay';

interface Tour {
  id: string;
  start_time_local: string;
  date: string;
  address: string;
  prospect_name: string;
  phone_number: string;
  move_in_date_requested: string;
  lease_length_requested: string;
  moving_reason: string;
  passed_screening: boolean;
  listing_id: string;
  prospect_id: string;
  status: string; 
  prospect?: {
    name: string;
    email: string;
    phone_number?: string;
    age_range?: string;
    new_renter?: boolean;
    has_year_long_rental?: boolean;
    current_living_situation?: string;
    living_situation_explained?: string;
    student?: boolean;
    undergraduate?: boolean;
    currently_employed?: boolean;
    employer?: string;
    occupation?: string;
    income_verifiable?: boolean;
    has_current_job_for_year?: boolean;
    rent_payment_without_job?: string;
    explain_rent_payment?: string;
    will_do_background_and_credit_check?: boolean;
    num_dogs?: number;
    num_cats?: number;
    lease_length_requested?: string;
    move_in_date_requested?: string;
    moving_reason?: string;
    number_of_applicants?: string;
    tour_preference?: string;
    short_intro?: string;
  } | null;
}

interface UpcomingToursCardProps {
  tour: Tour;
}

const UpcomingToursCard: React.FC<UpcomingToursCardProps> = ({ tour }) => {
  const initial = tour.prospect_name?.[0]?.toUpperCase() || '?';
  const [showDetails, setShowDetails] = useState(false);
  
  const handleShowDetails = () => {
    setShowDetails(true);
  };

  const handleCloseDetails = () => {
    setShowDetails(false);
  };

  const getStatusDisplayText = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'scheduled';
      case 'rescheduled':
        return <b>rescheduled</b>;
      case 'cancelled':
        return <b>cancelled</b>;
      default:
        return 'scheduled';
    }
  };

  return (
    <>
    <CustomCard sx={{ p: 2, maxWidth: '90%' }}>
      <Box display="flex" alignItems="flex-start" mb={1.5}>
        {/* Avatar */}
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            background: '#FFEB3B',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 500,
            fontSize: 24,
            color: '#222',
            mr: 2,
            flexShrink: 0,
            fontFamily: 'Inter, sans-serif',
          }}
        >
          {initial}
        </Box>
        {/* Details */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={700} sx={{ fontSize: 17, mb: 0.2, fontFamily: 'Inter, sans-serif' }}>
            {tour.prospect_name}
          </Typography>
          <Typography variant="body2" color="text.primary" sx={{ fontSize: 14, fontWeight: 400, mb: 0.2, fontFamily: 'Inter, sans-serif' }}>
            Tour {getStatusDisplayText(tour.status)} for {tour.start_time_local} est
          </Typography>
          <Typography variant="body2" color="text.primary" sx={{ fontWeight: 400, mb: 0.2, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: 220, fontFamily: 'Inter, sans-serif' }}>
            {tour.address}
          </Typography>
          <Box display="flex" alignItems="center" mt={0.5}>
            <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ fontFamily: 'Inter, sans-serif' }}>
              Passed AI screening
            </Typography>
            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18, ml: 0.5 }} />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400, mt: 0.2, fontFamily: 'Inter, sans-serif' }}>
            Phone: {formatPhoneNumber(tour.phone_number)}
          </Typography>
        </Box>
      </Box>
      <Box display="flex" gap={1.5} mt={1.5}>
        <SecondaryButton 
          fullWidth 
          sx={{ fontWeight: 600, fontSize: 15 }}
          onClick={handleShowDetails}
        >
          Prospect details
        </SecondaryButton>
      </Box>
      </CustomCard>

      {showDetails && tour.prospect && (
        <ProfileCardOverlay
          prospect={tour.prospect}
          onClose={handleCloseDetails}
        />
      )}
    </>
  );
};

export default UpcomingToursCard;